import { useCallback } from 'react';
import { useTranslate, useGetList } from 'react-admin';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Divider,
  Fab,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ConversationFilters } from './ConversationFilters';
import { ConversationListItem } from './ConversationListItem';
import { CreateConversationDialog } from './dialogs';
import { useConversationList } from '../hooks/useConversationList';
import type { Conversation, ConversationListPanelProps } from '../types/tickets.types';

/**
 * Skeleton loading placeholder matching ConversationListItem shape
 */
function ConversationListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index}>
          <ListItem dense sx={{ py: 1, px: 2 }}>
            <ListItemAvatar>
              <Skeleton animation="wave" variant="circular" width={48} height={48} />
            </ListItemAvatar>
            <ListItemText
              primary={<Skeleton animation="wave" height={18} width={`${60 + (index % 3) * 15}%`} />}
              secondary={<Skeleton animation="wave" height={14} width={`${70 + (index % 2) * 20}%`} />}
            />
          </ListItem>
          <Divider variant="inset" component="li" />
        </Box>
      ))}
    </>
  );
}

/**
 * ConversationListPanel - Left panel showing list of conversations
 *
 * Features:
 * - Responsive width: 280px (tablet) / 320px (desktop) / 360px (large desktop)
 * - Tab-based filters (All, Mine, Open Tickets)
 * - Sort dropdown (Last Activity, Created Date)
 * - Advanced filters (status, priority, queue, channel, assignment, archived)
 * - Infinite scroll pagination
 * - Improved skeleton loading states
 * - Shows unread count badges (inline)
 * - Displays typing indicators
 * - Queue color dots and assigned user info
 * - Contact tooltips with phone/email
 * - FAB for new conversation on mobile
 */
export function ConversationListPanel({
  selectedConversationId,
  onConversationSelect,
  isVisible = true,
  width,
  className,
}: ConversationListPanelProps) {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    conversations,
    filterState,
    unreadCountMap,
    typingUsersMap,
    isLoading,
    hasMore,
    error,
    setFilterOptions,
    setActiveFilter,
    setSearchQuery,
    setSortBy,
    loadMore,
  } = useConversationList();

  // Fetch available queues and channels for filter dropdowns
  const { data: queues = [] } = useGetList('ticket-queue', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  const { data: channels = [] } = useGetList('channel', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: { isEnabled_eq: true },
  });

  const availableQueues = queues.map((q: { uuid: string; name: string }) => ({
    uuid: q.uuid,
    name: q.name,
  }));

  const availableChannels = channels.map((c: { uuid: string; name: string }) => ({
    uuid: c.uuid,
    name: c.name,
  }));

  // Infinite scroll handler
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!hasMore || isLoading) return;

      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - (scrollTop + 100) < clientHeight) {
        loadMore();
      }
    },
    [hasMore, isLoading, loadMore]
  );

  if (!isVisible) {
    return null;
  }

  const isInitialLoad = isLoading && conversations.length === 0;
  const isLoadingMore = isLoading && conversations.length > 0;

  return (
    <Paper
      variant="outlined"
      className={className}
      sx={{
        width: width || { xs: '100%', md: 280, lg: 320, xl: 360 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 0,
        borderTop: 0,
        borderBottom: 0,
        borderLeft: 0,
        position: 'relative',
      }}
    >
      {/* Header with Create Conversation button */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 52,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
          {translate('resources.conversations.name')}
        </Typography>

        {!isMobile && (
          <CreateConversationDialog
            onSuccess={(conversationId, conversationUuid) => {
              if (onConversationSelect) {
                const conv = { id: conversationId, uuid: conversationUuid } as Conversation;
                onConversationSelect(conv);
              }
            }}
          />
        )}
      </Box>

      {/* Filters + Sort Component */}
      <ConversationFilters
        filterState={filterState}
        onFilterChange={setFilterOptions}
        onTabChange={setActiveFilter}
        onSearchChange={setSearchQuery}
        onSortChange={setSortBy}
        sortBy={filterState.options.sortBy as 'lastActivityAt' | 'createdAt' || 'lastActivityAt'}
        availableQueues={availableQueues}
        availableChannels={availableChannels}
      />

      {/* Conversation list with infinite scroll */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          bgcolor: 'background.default',
          position: 'relative',
        }}
        onScroll={handleScroll}
      >
        {isInitialLoad ? (
          <ConversationListSkeleton />
        ) : error ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            p={3}
            height="100%"
          >
            <Typography color="error" gutterBottom>
              {translate('ra.notification.error')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {error instanceof Error ? error.message : 'Unknown error'}
            </Typography>
          </Box>
        ) : conversations && conversations.length > 0 ? (
          <List disablePadding>
            {conversations.map((conversation) => (
              <ConversationListItem
                key={conversation.id}
                conversation={conversation}
                isSelected={conversation.id === selectedConversationId}
                onClick={onConversationSelect}
                typingUsers={typingUsersMap[Number(conversation.id)] || []}
                unreadCount={unreadCountMap[Number(conversation.id)] || 0}
                showTicketBadge
              />
            ))}

            {/* Loading more indicator */}
            {isLoadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </List>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            p={4}
            height="100%"
          >
            <Typography variant="h6" color="textSecondary" gutterBottom sx={{ fontSize: '1rem' }}>
              {translate('resources.conversations.empty.title')}
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center">
              {translate('resources.conversations.empty.subtitle')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* FAB for new conversation on mobile */}
      {isMobile && (
        <CreateConversationDialog
          onSuccess={(conversationId, conversationUuid) => {
            if (onConversationSelect) {
              const conv = { id: conversationId, uuid: conversationUuid } as Conversation;
              onConversationSelect(conv);
            }
          }}
          trigger={
            <Tooltip title={translate('resources.conversations.createConversation')}>
              <Fab
                color="primary"
                size="medium"
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  zIndex: 1,
                }}
              >
                <AddIcon />
              </Fab>
            </Tooltip>
          }
        />
      )}
    </Paper>
  );
}
